"use client";

/**
 * ToolSkeleton - Loading placeholder component
 *
 * Provides loading state placeholders for different content types
 */

import { Card, Skeleton, SkeletonText } from "@/ui";
import { colors, spacing, radius, primitives } from "@/ui/tokens";
import { cn } from "@/lib/utils";

export interface ToolSkeletonProps {
  variant?: "card" | "list" | "table" | "text";
  rows?: number;
  className?: string;
}

/**
 * Card skeleton - single card placeholder
 */
function CardSkeleton() {
  return (
    <Card
      variant="outlined"
      padding="sm"
      style={{ borderRadius: radius.md }}
    >
      <div style={{ display: "flex", gap: spacing[4], alignItems: "flex-start" }}>
        <Skeleton variant="circular" width={48} height={48} />
        <div style={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="80%" />
        </div>
      </div>
    </Card>
  );
}

/**
 * List skeleton - multiple list items
 */
function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: spacing[4],
            alignItems: "center",
            padding: spacing[3],
            borderRadius: radius.sm,
            backgroundColor: primitives.neutral[100],
          }}
        >
          <Skeleton variant="circular" width={32} height={32} />
          <div style={{ flex: 1 }}>
            <Skeleton variant="text" width={`${60 + (i % 3) * 10}%`} />
          </div>
          <Skeleton variant="rounded" width={60} height={24} />
        </div>
      ))}
    </div>
  );
}

/**
 * Table skeleton - table placeholder
 */
function TableSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <Card
      variant="outlined"
      padding="none"
      style={{ borderRadius: radius.md, overflow: "hidden" }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          gap: spacing[4],
          padding: spacing[3],
          backgroundColor: primitives.neutral[100],
          borderBottom: `1px solid ${colors.border.subtle}`,
        }}
      >
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="text" width={`${25 + i * 5}%`} />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: spacing[4],
            padding: spacing[3],
            borderBottom: i < rows - 1 ? `1px solid ${colors.border.subtle}` : "none",
          }}
        >
          {[1, 2, 3].map((j) => (
            <Skeleton
              key={j}
              variant="text"
              width={`${20 + ((i + j) % 4) * 10}%`}
            />
          ))}
        </div>
      ))}
    </Card>
  );
}

/**
 * Text skeleton - paragraph placeholder
 */
function TextSkeleton({ rows = 3 }: { rows?: number }) {
  return <SkeletonText lines={rows} />;
}

export function ToolSkeleton({
  variant = "card",
  rows = 3,
  className,
}: ToolSkeletonProps) {
  return (
    <div className={cn(className)}>
      {variant === "card" && <CardSkeleton />}
      {variant === "list" && <ListSkeleton rows={rows} />}
      {variant === "table" && <TableSkeleton rows={rows} />}
      {variant === "text" && <TextSkeleton rows={rows} />}
    </div>
  );
}

export default ToolSkeleton;
